import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';

import beUpgradePlan from 'actions/billing/beUpgradePlan.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Spinner from 'components/generic/Spinner.component';
import DELAY from 'constants/delay.const';
import PLAN_INDEX from 'constants/planIndex.const';
import S from 'constants/string.const';
import { FeStack } from 'errors/fe.error';
import useAef from 'hooks/useAef.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import {
  selectCurrentSalonBetaAnnualSalon,
  selectCurrentSalonBilling,
  selectCurrentSalonId,
  selectCurrentSalonPricingPlan,
} from 'selectors/currentSalon.selectors';
import { selectBillingError, selectBillingPlan } from 'selectors/settings.selectors';
import { locationReload } from 'utils/dom/location.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import salonSubscription from 'utils/salons/salonSubscription.util';

import { getBillingPlans } from 'settings/billing/actions';
import BetaAnnualModal from 'settings/online-booking/modals/BetaAnnualModal';
import NoBillingModal from 'settings/online-booking/modals/NoBillingModal';

import CN from './UpgradePlanButton.module.scss';

const CTAG = 'UpgradePlanButton';
const L = logger(CTAG);

const SPINNER_COLOR = '#821bb8';
const SPINNER_BACKGROUND_COLOR = 'white';

const UpgradePlanButton = ({
  salonId,
  beUpgradePlan,
  getBillingPlans,
  hasBilling,
  text,
  pricingPlan,
  children,
  hasBetaAnnualSalon,
  variantWhite,
}) => {
  const { t } = useTranslation();
  const { val: isUpgradeRendered, tip: toggleUpgrade } = useTip(false);
  const { val: isBillingRendered, yay: showBilling, nay: hideBilling } = useTip(false);
  const { val: isBetaAnnualRendered, yay: showBetaAnnual, nay: hideBetaAnnual } = useTip(false);
  const { val: isLoading, yay: startLoading, nay: stopLoading } = useTip(false);

  const handleUpgrade = useCallback(() => {
    if (hasBilling && hasBetaAnnualSalon) showBetaAnnual();
    if (!hasBilling && !hasBetaAnnualSalon) showBilling();
    if (hasBilling && !hasBetaAnnualSalon) toggleUpgrade();
  }, [hasBetaAnnualSalon, hasBilling, showBetaAnnual, showBilling, toggleUpgrade]);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!salonId) return;

    toggleUpgrade();
    startLoading();

    try {
      const options = { planId: PLAN_INDEX.advanced, salonId };
      await L.unignore('handleConfirmUpgrade()', 'beUpgradePlan:', beUpgradePlan(options));
    } finally {
      delayed(DELAY.flash, () => {
        locationReload(new FeStack({ tag: 'UpgradePlanButton__confirm-upgrade' }));
        stopLoading();
      });
    }
  }, [salonId, beUpgradePlan, toggleUpgrade, stopLoading, startLoading]);

  const { error } = useAef(async () => {
    startLoading();
    try {
      await getBillingPlans(salonId);
    } finally {
      delayed(DELAY.flash, stopLoading);
    }
  }, [startLoading, getBillingPlans, salonId, stopLoading]);

  if (error) {
    L.warn('()', error);
  }

  const isComponentRendered = salonSubscription(hasBilling, pricingPlan);

  const displayPlanName = pricingPlan || 'Custom';

  if (!isComponentRendered) {
    return null;
  }

  return (
    <div data-bem="UpgradePlanButton">
      <div className={CN.component}>
        <Button variant={variantWhite ? 'outline' : 'primaryForce'} onClick={handleUpgrade}>
          {text}
          {children}
        </Button>
      </div>

      {isLoading && <Spinner color={SPINNER_COLOR} message="" background={SPINNER_BACKGROUND_COLOR} />}

      {isBillingRendered && <NoBillingModal isOpen onClose={hideBilling} />}

      {isBetaAnnualRendered && <BetaAnnualModal planName={displayPlanName} isOpen onClose={hideBetaAnnual} />}

      {isUpgradeRendered && (
        <SalonModal tag="UpgradePlanButton" isOpen>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('composite.upgradePlanModal.title', { suffix: S.fist })}</div>
              <div className={CN.modalText}>{t('composite.upgradePlanModal.descriptionPart1')}</div>
              <div className={CN.modalText}>{t('composite.upgradePlanModal.descriptionPart2')}</div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={toggleUpgrade}>
              {t('composite.upgradePlanModal.cancel')}
            </Button>
            <Button variant="primaryForce" casing="dialog" xSpace onClick={handleConfirmUpgrade}>
              {t('composite.upgradePlanModal.confirm')}
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  billingPlan: selectBillingPlan(state),
  salonId: selectCurrentSalonId(state),
  errorMessage: selectBillingError(state),
  hasBilling: selectCurrentSalonBilling(state),
  pricingPlan: selectCurrentSalonPricingPlan(state),
  hasBetaAnnualSalon: selectCurrentSalonBetaAnnualSalon(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ getBillingPlans, beUpgradePlan }, dispatch);

const UpgradePlanButtonHoc = withRouter(connect(mapStateToProps, mapDispatchToProps)(UpgradePlanButton));

export default UpgradePlanButtonHoc;
